import React, { useCallback, useContext, useRef, useState } from 'react';
import MainPanel from './control_panel/MainPanel';
import ZoomPanel from './control_panel/ZoomPanel';
import type { TImageContentItem } from '@src/types/wysiwyg_editor';
import { useDispatch } from 'react-redux';

import ControlPanelFrame from '@src/components/wysiwyg_editor/ControlPanelFrame';
import type { ReactRefT } from '@src/types/misc';
import PreContentPanel from '@src/components/wysiwyg_editor/page_content_items/image/control_panel/PreContentPanel';
import usePageContentItemCount from '@src/hooks/wysiwyg_editor/usePageContentItemCount';
import { WysiwygEditorDispatchContext } from '@src/contexts/wysiwyg/editor';
import { updateContentItem } from '@src/actions/wysiwyg/editor';

type TProps = {
  anchorLocation: {
    x: number;
    y: number;
    height: number;
    width: number;
  };
  anchorRef: ReactRefT<HTMLElement>;
  imageData: TImageContentItem;
  stackOrder: number;
  zoomLevel: number;
  onImageChange: () => void;
  onZoomChange: (newZoom: number, isDone: boolean) => void;
  onZoomDragStart: () => void;
};

const PANELS = { main: 'MAIN', pre_content: 'PRE_CONTENT', zoom: 'ZOOM' };
const CONTROL_PANEL_DIMENSIONS = {
  [PANELS.main]: { height: 105, width: 375 },
  [PANELS.pre_content]: { height: 105, width: 83 },
  [PANELS.zoom]: { height: 105, width: 170 }
};

function ControlPanel({
  anchorLocation,
  anchorRef,
  imageData,
  stackOrder,
  onImageChange,
  onZoomChange,
  onZoomDragStart,
  zoomLevel
}: TProps): React.ReactElement {
  const dispatch = useDispatch();
  const [currentPanel, setCurrentPanel] = useState(
    imageData.href ? PANELS.main : PANELS.pre_content
  );
  const controlPanelRef = useRef<HTMLDivElement | null | undefined>(null);
  const unmodifiedDataRef = useRef<TImageContentItem>(imageData);

  const handleAcceptZoom = useCallback(() => {
    setCurrentPanel(PANELS.main);
  }, []);

  const handleDiscardZoom = useCallback(() => {
    dispatch(
      updateContentItem(unmodifiedDataRef.current.id, unmodifiedDataRef.current)
    );
    setCurrentPanel(PANELS.main);
  }, [dispatch]);

  return (
    <ControlPanelFrame
      size={CONTROL_PANEL_DIMENSIONS[currentPanel]}
      anchorRef={anchorRef}
      anchorRect={anchorLocation}
      ref={controlPanelRef}
      zIndex={stackOrder}
    >
      {currentPanel === PANELS.main ? (
        <MainPanel
          itemData={imageData}
          onChangeClick={onImageChange}
          onZoomClick={() => setCurrentPanel(PANELS.zoom)}
        />
      ) : currentPanel === PANELS.pre_content ? (
        <PreContentPanel itemId={imageData.id} />
      ) : currentPanel === PANELS.zoom ? (
        <ZoomPanel
          currentZoom={zoomLevel}
          onAcceptClick={handleAcceptZoom}
          onDiscardClick={handleDiscardZoom}
          onZoomChange={onZoomChange}
          onZoomDragStart={onZoomDragStart}
        />
      ) : null}
    </ControlPanelFrame>
  );
}

export default ControlPanel;
